import React from "react";
import { ArticleChangeImportance, CommandResult, SubscriberArticle } from "../../../../amorg-common";
import { articleValidation } from "../../../../amorg-common/modules/fieldValidation/fields/articleValidation";
import { ImportanceAvatar } from "../../../../designSystem/avatar/ImportanceAvatar";
import { Button } from "../../../../designSystem/button/Button";
import { Flex } from "../../../../designSystem/compositeComponents/FlexHelpers";
import { FormField } from "../../../../designSystem/formField/FormField";
import { HtmlEditorViewer } from "../../../../designSystem/inputs/HtmlEditorViewer";
import { HtmlEditorWithAutoSave } from "../../../../designSystem/inputs/HtmlEditorWithAutoSave";
import { Label } from "../../../../designSystem/Label";
import { txt } from '../../../../txt';
import { toDateTimeString } from "../../../../utils/date";
import { ArticleImportanceSelector } from "../../_components/ArticleImportanceSelector";

type ChangeEvaluationFieldsProps = {
    article: SubscriberArticle,
    latestVersion: boolean,
    onCreateEvaluation: (articleVersionId: string) => Promise<CommandResult>,
    onUpdateEvaluation: (articleVersionId: string, evaluationText: string, evaluationImportance: ArticleChangeImportance | null) => Promise<CommandResult>;
}
export function ChangeEvaluationFields (props: ChangeEvaluationFieldsProps): JSX.Element
{
    const [evaluationError, setEvaluationError] = React.useState<string>('');

    async function handleSaveEvaluationText (text: string):Promise<boolean>
    {
        let error = '';
        if(!articleValidation.siteArticleEvaluation.evaluationText(text))
        {
            error = `Evalueringen skal udfyldes og må maksimalt indeholde 20000 (${text.length}) tegn `;
        }
        else {
            const result = await props.onUpdateEvaluation(props.article.articleVersionId, text, props.article.evaluation?.evaluationImportance || null);
            error = result.isSuccess
                ? ''
                : 'Der skete en fejl under gemning af evalueringen. Prøv igen.';            
        }

        setEvaluationError(error);
        return !error;
    }

    return (
        <div>
            <Flex flexDirection='column' gap={16}>

                <ChangeFields key={props.article.articleVersionId}
                    article={props.article}
                />

                <Flex flexDirection='column' gap={2}>
                    <Flex justifyContent='space-between' alignItems='flex-end'>
                        <Label>Evaluering</Label>

                        {props.latestVersion &&
                            <>
                                {props.article.evaluation !== null &&
                                    <Flex justifyContent='flex-end' alignItems='center' gap={8}>

                                        <ArticleImportanceSelector
                                            mode='evaluation'
                                            selectedImportance={props.article.evaluation.evaluationImportance}
                                            onChangeImportance={(importance) =>
                                                props.onUpdateEvaluation(
                                                        props.article.articleVersionId,
                                                        props.article.evaluation?.evaluationText || '',
                                                        importance
                                                )
                                            }
                                        />

                                    </Flex>
                                }
                            </>
                        }

                        {!props.latestVersion &&
                            <div style={{paddingRight:'20px'}}>
                                <ReadOnlyEvaluationField importance={props.article.evaluation?.evaluationImportance || undefined} />
                            </div>
                        }

                    </Flex>

                    {props.article.evaluation !== null && 
                        <>
                            {props.latestVersion && 
                                <>
                                    <div style={{ flexGrow: 1, marginBottom: 10 }}>
                                        <HtmlEditorWithAutoSave
                                            id="article-evaluation-text-html-editor"
                                            errorMessage={evaluationError}
                                            value={props.article.evaluation.evaluationText}
                                            onSave={handleSaveEvaluationText}
                                            onCancel={() => setEvaluationError('')} />
                                    </div>
                                    <div>
                                        opdateret d. {toDateTimeString(props.article.evaluation.lastEdited.date as Date)} af {props.article.evaluation.lastEdited.name}
                                    </div>
                                </>
                            }
                            {!props.latestVersion && props.article.evaluation.evaluationText &&
                                <HtmlEditorViewer content={props.article.evaluation.evaluationText} />
                            }
                        </>
                    }
                </Flex>



                {props.latestVersion && props.article.evaluation === null &&
                    <Flex justifyContent='space-between' alignItems='center' flexWrap="wrap" gap={16}>
                        <div>Der er ikke oprettet en evaluering af lovkravsversionen</div>
                        <Button variant='filled' text="Opret evaluering" onClick={() => props.onCreateEvaluation(props.article.articleVersionId)} />
                    </Flex>
                }

            </Flex>
        </div>
    );
}

type ChangeFieldsProps = {
    article: SubscriberArticle
};
export function ChangeFields (props: ChangeFieldsProps): JSX.Element
{
    return (
        <>
            {props.article.publication.changeDescription && props.article.publication.changeDescription !== '<p><br></p>' &&
                <FormField label="Ændringsbeskrivelse"
                    inputComponent={<HtmlEditorViewer content={props.article.publication.changeDescription} />} />
            }

            {props.article.publication.assessment && props.article.publication.assessment !== '<p><br></p>' &&
                <FormField label="Vurdering"
                    inputComponent={<HtmlEditorViewer content={props.article.publication.assessment} />} />
            }
        </>
    );
}

function ReadOnlyEvaluationField({importance}:{importance?:ArticleChangeImportance}):JSX.Element
{
    return (
        <Flex gap={8} alignItems="center">
            <ImportanceAvatar importance={importance} mode='evaluation' />
            {!importance  &&
                <div>Ikke evalueret</div>
            }
            {importance === 'low' &&
                <div>{txt.ArticleEvaluation_low_text}</div>
            }
            {importance === 'medium' &&
                <div>{txt.ArticleEvaluation_medium_text}</div>
            }
            {importance === 'high' &&
                <div>{txt.ArticleEvaluation_high_text}</div>
            }
        </Flex>
    );
}